import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart, formatINR } from "@/lib/cart";
import { Trash2, ShoppingCart, ArrowRight, ArrowLeft } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { fileTypeIcon, fileTypeLabelKey } from "@/lib/fileTypes";

const GST_PERCENT = 18;

export default function Cart() {
  const { t } = useI18n();
  const { items, removeItem, subtotalPaise, count } = useCart();
  const nav = useNavigate();

  const gst = Math.floor((subtotalPaise * GST_PERCENT) / 100);
  const total = subtotalPaise + gst;

  if (count === 0) {
    return (
      <div className="bg-slate-50/50 min-h-[60vh]" data-testid="cart-page-empty">
        <div className="nc-container py-16 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
            <ShoppingCart className="w-8 h-8 text-emerald-700" />
          </div>
          <h1 className="mt-4 text-2xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{t("cart.empty")}</h1>
          <p className="mt-2 text-slate-600">{t("cart.emptySub")}</p>
          <Link to="/plans" className="mt-6 inline-flex nc-btn-primary">{t("cart.browse")}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50/50 min-h-screen" data-testid="cart-page">
      <div className="bg-white border-b border-slate-200">
        <div className="nc-container py-3">
          <Link to="/plans" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-700">
            <ArrowLeft className="w-4 h-4" /> {t("cart.continueBrowse")}
          </Link>
        </div>
      </div>

      <div className="nc-container py-8 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-3">
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{t("cart.title")} ({count})</h1>
          <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
            {items.map((it, idx) => {
              const Icon = fileTypeIcon(it.file_type);
              return (
                <div key={`${it.plan_id}-${it.file_type}`} className="flex items-center gap-3 p-4" data-testid={`cart-item-${idx}`}>
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/plans/${it.plan_slug || it.plan_id}`} className="block font-semibold text-slate-900 hover:text-emerald-700 truncate">
                      {it.plan_title}
                    </Link>
                    <p className="text-xs text-slate-500">{t(fileTypeLabelKey(it.file_type))} • {t("cart.pdfWatermarked")}</p>
                  </div>
                  <div className="text-sm font-bold text-slate-900 w-24 text-right">{formatINR(it.price_paise)}</div>
                  <button onClick={() => removeItem(it.plan_id, it.file_type)} className="p-2 text-slate-400 hover:text-rose-600 transition" aria-label="remove" data-testid={`remove-cart-item-${idx}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-4">
          <div className="sticky top-20 bg-white rounded-2xl border border-slate-200 p-5">
            <h2 className="font-semibold text-slate-900 text-base">{t("cart.summary")}</h2>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">{t("cart.subtotal")} ({count} {count > 1 ? t("cart.itemPlural") : t("cart.itemSingular")})</span>
                <span className="font-medium text-slate-900">{formatINR(subtotalPaise)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">{t("cart.gst")} ({GST_PERCENT}%)</span>
                <span className="font-medium text-slate-900">{formatINR(gst)}</span>
              </div>
              <div className="border-t border-slate-200 pt-2 mt-2 flex items-center justify-between">
                <span className="font-semibold text-slate-900">{t("cart.total")}</span>
                <span className="text-lg font-bold text-slate-900" data-testid="cart-total">{formatINR(total)}</span>
              </div>
            </div>
            <button
              onClick={() => nav("/checkout")}
              className="mt-5 nc-btn-primary w-full justify-center"
              data-testid="proceed-checkout-btn"
            >
              {t("cart.proceed")} <ArrowRight className="w-4 h-4" />
            </button>
            <p className="mt-3 text-[11px] text-slate-500 leading-relaxed text-center">
              {t("cart.gstNote")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
